import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Breadcrumbs,
  Chip,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PercentIcon from "@mui/icons-material/Percent";

function TaxSettings() {
  const navigate = useNavigate();
  const [taxValue, setTaxValue] = useState({
    tax: 0,
    user_id: Helpers.authParentId,
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [perms, setPerms] = useState({});

  const getTax = useCallback(() => {
    axios
      .get(
        `${Helpers.baseUrl}/customers/get-tax/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        if (response.data != null) {
          setTaxValue(response.data);
        }
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      });
  }, [navigate]);

  const getPerms = () => {
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find(
        (permission) => permission.tab_link === "/user/roles",
      );
      setPerms(perm || {});
    }
  };

  const handleSave = () => {
    setBtnLoading(true);
    axios
      .post(
        `${Helpers.baseUrl}/customers/update-tax`,
        taxValue,
        Helpers.headers,
      )
      .then(() => {
        getTax();
        Helpers.toast("success", "Tax updated successfully");
        setBtnLoading(false);
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
        setBtnLoading(false);
      });
  };

  useEffect(() => {
    getTax();
    getPerms();
  }, [getTax]);

  const canUpdate =
    Helpers.authUser.user_role == null || perms.can_update === 1;

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: "rgba(255, 255, 255, 0.06)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Dashboard
            </MuiLink>
            <MuiLink
              component={Link}
              to="/user/settings"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Settings
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Tax Settings
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              position: "relative",
              zIndex: 1,
              flexWrap: "wrap",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <PercentIcon sx={{ fontSize: 38, color: "white" }} />
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: "white", fontWeight: 700, mb: 0.5 }}
                >
                  Tax Settings
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
                >
                  Set the default tax percentage for taxable customers.
                </Typography>
              </Box>
            </Stack>

            <Chip
              icon={<PercentIcon sx={{ color: "white !important" }} />}
              label={`Tax: ${taxValue.tax || 0}%`}
              size="small"
              sx={{
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            p: 3,
            maxWidth: 640,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Tax Percentage for Taxable Customers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This value is used as the default sales tax rate for taxable
            customers in the system.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "flex-start" }}
          >
            <TextField
              label="Tax Value (%)"
              type="number"
              size="small"
              value={taxValue.tax}
              onChange={(e) =>
                setTaxValue({ ...taxValue, tax: e.target.value })
              }
              sx={{ width: { xs: "100%", sm: 220 } }}
            />
            {canUpdate && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={btnLoading}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#28a745",
                  "&:hover": { backgroundColor: "#218838" },
                }}
              >
                {btnLoading ? "Updating..." : "Update Tax"}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default TaxSettings;
